import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Server, Check, Rocket, TrendingUp, PiggyBank, Mail, Calculator, ExternalLink } from "lucide-react";
import { computeScope, calculatorQuery } from "@/lib/hosting";
import MigrationEmailDialog from "@/components/MigrationEmailDialog";

// Detected-host + dependency migration pitch. Reused in the full report so paid
// users see exactly what a migration off their current host would cover.
export const MigrationPitch = ({ data, comp = {}, seo = {}, sec = {}, showEmail = true }) => {
  const navigate = useNavigate();
  const [emailOpen, setEmailOpen] = useState(false);
  const hosting = data.hosting || {};
  const secGaps = [];
  if (!sec.hsts) secGaps.push({ severity: "warning" });
  if (!sec.csp) secGaps.push({ severity: "warning" });
  const scope = computeScope({ data, comp, seo, secGaps });

  const deps = (hosting.dependencies || []).slice(0, 6);
  const depsExtra = (hosting.dependencies || []).length - deps.length;
  const hostName = hosting.platform || hosting.hostLabel || null;
  const savings = hosting.savings || {};
  const savMap = Object.fromEntries((savings.items || []).map((i) => [i.name, i.monthly]));
  const swaps = (savings.items || []).filter((i) => !i.foldsIn && i.recommended);
  const calcHref = calculatorQuery(hosting);
  const pitch = hosting.platform
    ? `You're on ${hosting.platform}. We'll move you off it — with zero downtime.`
    : hosting.hostLabel
    ? `You're hosted on ${hosting.hostLabel}. We'll move & modernize your setup — with zero downtime.`
    : `We'll move & modernize your current setup — with zero downtime.`;
  const quoteHref = `/pricing?domain=${encodeURIComponent(data.domain)}&scope=${scope.key}#intake`;

  return (
    <div className="relative overflow-hidden rounded-xl border border-[#9b5cff]/30 p-5 mb-6" style={{ background: "linear-gradient(135deg, rgba(155,92,255,0.12), rgba(0,122,255,0.10))" }} data-testid="migration-pitch">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-[#9b5cff]/15 p-2.5 shrink-0">
          <Server size={18} className="text-[#c9a6ff]" />
        </div>
        <div className="min-w-0 w-full">
          {hostName && (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 mb-2" data-testid="detected-host">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">Detected host</span>
              <span className="text-sm font-semibold text-white">{hostName}</span>
              {hosting.framework && hosting.framework !== "None / Vanilla" && (
                <span className="text-xs text-zinc-400">· {hosting.framework}</span>
              )}
            </div>
          )}
          <h3 className="font-heading text-lg font-bold text-white tracking-tight" data-testid="migration-pitch-headline">{pitch}</h3>
          {deps.length > 0 && (
            <>
              <p className="text-zinc-400 text-sm mt-3">We'll carry over everything you rely on:</p>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="migration-dependencies">
                {deps.map((d, i) => (
                  <span key={i} data-testid={`migration-dep-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-[#0d0d0d] px-3 py-1.5 text-xs text-zinc-200">
                    <Check size={12} className="text-[#32D74B]" /> {d.name}
                    {savMap[d.name] > 0 && <span className="text-[#32D74B] font-medium">· ${Math.round(savMap[d.name])}/mo</span>}
                  </span>
                ))}
                {depsExtra > 0 && (
                  <span className="inline-flex items-center rounded-full border border-dashed border-white/15 px-3 py-1.5 text-xs text-zinc-500" data-testid="migration-deps-more">+ {depsExtra} more</span>
                )}
              </div>
            </>
          )}
          {savings.totalMonthly > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#32D74B]/30 bg-[#32D74B]/10 px-3 py-2" data-testid="savings-estimate">
              <PiggyBank size={15} className="text-[#32D74B]" />
              <span className="text-sm text-white">Consolidate on Emergent → save <b>~${Math.round(savings.totalMonthly)}/mo</b> <span className="text-zinc-400">(~${Math.round(savings.totalAnnual)}/yr, estimated)</span></span>
            </div>
          )}
          {swaps.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400" data-testid="affiliate-swaps">
              <span>Best-value swaps:</span>
              {swaps.map((it, i) => (
                it.recommended?.url ? (
                  <a key={i} data-testid={`affiliate-swap-${i}`} href={it.recommended.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#4da3ff] hover:text-white transition-colors">
                    {it.name} → {it.recommended.name} <ExternalLink size={10} />
                  </a>
                ) : (
                  <span key={i} data-testid={`affiliate-swap-${i}`} className="inline-flex items-center gap-1 text-zinc-300">
                    {it.name} → {it.recommended.name}
                  </span>
                )
              ))}
            </div>
          )}
          {scope.heavy && scope.platform && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-zinc-400" data-testid="scope-nudge-note">
              <TrendingUp size={13} className="text-[#FFD60A]" />
              {scope.nudged
                ? <span>Estimate raised from <b className="text-white">{scope.baseLabel}</b> to <b className="text-white">{scope.label}</b> — {scope.platform} sites carry more moving parts.</span>
                : <span>{scope.platform} sites carry more moving parts — factored into your <b className="text-white">{scope.label}</b> estimate.</span>}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button data-testid="migration-quote-btn" onClick={() => navigate(quoteHref)} className="btn-brand text-white rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2">
              <Rocket size={15} /> Get a free migration quote ({scope.emoji} {scope.label})
            </button>
            {showEmail && (
              <button data-testid="email-pitch-btn" onClick={() => setEmailOpen(true)} className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 transition-colors">
                <Mail size={15} /> Email this pitch
              </button>
            )}
            <button data-testid="savings-calc-btn" onClick={() => navigate(calcHref)} className="text-[#4da3ff] hover:text-white text-sm inline-flex items-center gap-1.5 transition-colors">
              <Calculator size={14} /> See full savings breakdown
            </button>
          </div>
        </div>
      </div>
      <MigrationEmailDialog open={emailOpen} onOpenChange={setEmailOpen} domain={data.domain} hosting={hosting} scopeLabel={scope.label} />
    </div>
  );
};

export default MigrationPitch;
